import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { forkJoin, Subscription } from "rxjs";
import {
  DropdownService,
  Question,
  GlobalQuestionService,
  SubcategoryService,
  SubgroupService,
  TaxReturnService,
  TaxReturnTypeService,
  TaxReturnType
} from "../../../../../../swaggerServices";
import {
  ListOption,
  makeControlForQuestion,
  IFormField
} from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { cloneDeep } from "lodash-es";
import { GlobalQuestionsResolver } from "../global-questions-resolver";
import { IListOption } from "../../../../../shared/components/search-dropdown/search-dropdown.component";
import { environment } from "../../../../../environments/environment";
import { map } from "rxjs/operators";

declare var $: any;

export interface IQuestionWithScAndSg extends IFormField {
  subCategory?: {
    id: number;
    questions: IFormField[];
  };
  subGroup?: {
    id: number;
    questions: IFormField[];
  };
  questionDropdown?: IListOption[];
  APSDimension?: number;
  APSOrder?: number;
  APSCalcField?: boolean;
  tacRef?: string;
  lodgementBotField?: string;
  taxReturnTypeId?: number;
}

@Component({
    selector: "app-global-question",
    templateUrl: "./global-question.component.html",
    styleUrls: ["./global-question.component.scss"],
    standalone: false
})
export class GlobalQuestionComponent implements OnInit, OnDestroy {
  question: IQuestionWithScAndSg;
  originalQuestion: IQuestionWithScAndSg;
  questions: Question[];
  formGroup: UntypedFormGroup;
  prevFormGroup: UntypedFormGroup;

  @ViewChild("questionTypeDropDown") questionTypeDropDown: ElementRef;
  @ViewChild("defaultValueDropdown") defaultValueDropdown: ElementRef;
  @ViewChild("numberOptionsDropDown") numberOptionsDropDown: ElementRef;
  @ViewChild("addQuestionModal") addQuestionModal: ElementRef;

  dependencyType: string;
  dependantMandatoryQuestion: IQuestionWithScAndSg;
  tab: number = 1;
  clickedRow: boolean;
  questionType: UntypedFormControl;
  questionName: UntypedFormControl;
  mandatory: UntypedFormControl;
  topLevel: UntypedFormControl;
  filtering: UntypedFormControl;
  hidden: UntypedFormControl;
  complexityOverride: UntypedFormControl;
  default: UntypedFormControl;
  min: UntypedFormControl;
  max: UntypedFormControl;
  maxLength: UntypedFormControl;
  valueFormat: UntypedFormControl;
  allowDecimals: UntypedFormControl;
  hint: UntypedFormControl;
  tag: UntypedFormControl;
  isTable: UntypedFormControl;
  alwaysEditable: UntypedFormControl;
  questionSearch: string;
  columnQuestions: IFormField[] = []; // ids of questions which need to be saved with parentId on save
  isSaving: boolean;
  isSyncingCache: boolean;
  isProductionMode: boolean;
  taxpayerTaxReturnTypeId: number;
  subscriptions: Subscription[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private questionService: GlobalQuestionService,
    private questionsResolver: GlobalQuestionsResolver,
    private router: Router,
    private taxReturnService: TaxReturnService,
    private typeService: TaxReturnTypeService,
    private subcategoryService: SubcategoryService,
    private subgroupService: SubgroupService,
    private dropdownService: DropdownService
  ) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.questionsResolver.getQuestionsObs().subscribe((q: Question) => {
      this.initQuestion(q);
    });
  }

  getQuestions() {
    this.subscriptions.push(
      this.questionService.getGlobalQuestions().subscribe((q: any) => {
        this.questions = q.questions;
        this.initTableQuestions();
      })
    );
  }

  // find all question table's column questions
  initTableQuestions() {
    this.columnQuestions = [];
    this.questions.forEach(q => {
      if (q.parentId === this.question.id) this.columnQuestions.push(q);
    });
  }

  unfocusValueRows() {
    if (!this.question.questionDropdowns) return;
    this.question.questionDropdowns.forEach(
      (v: ListOption) => (v.edit = false)
    );
  }

  getQuestionType(q: IFormField): string {
    let questionType = q.displayFieldType;
    if (q.displayFieldType === "text") {
      if (q.dataType === "string") {
        questionType = "text";
      } else if (
        q.dataType.toLowerCase() === "integer" ||
        q.dataType.toLowerCase() === "float"
      ) {
        questionType = "number";
      }
    }
    return questionType;
  }

  initQuestion(q: IFormField) {
    if (!q.questionDropdowns) q.questionDropdowns = [];
    q.questionDropdowns = q.questionDropdowns.sort((a, b) => a.order - b.order); // order dropdown values by selected order

    this.originalQuestion = cloneDeep(q);
    this.dependencyType =
      q.dependantMandatoryParentValue && q.dependantMandatoryParentId
        ? "specific"
        : "any";

    // answers is req by field-wrapper
    q.answers = [];
    this.question = q;

    // for table questions, always refresh questions list
    if (this.question.displayFieldType === "table") {
      this.getQuestions();
    } else if (!this.questions || this.questions.length === 0) {
      this.getQuestions();
    }

    // init forms
    this.formGroup = new UntypedFormGroup({});
    this.prevFormGroup = new UntypedFormGroup({});
    this.questionType = new UntypedFormControl(this.getQuestionType(q));
    this.hidden = new UntypedFormControl(this.question.hidden || false);
    this.complexityOverride = new UntypedFormControl(
      this.question.complexityOverride || false
    );
    this.topLevel = new UntypedFormControl(this.question.topLevel || false);
    this.questionName = new UntypedFormControl(this.question.question);
    this.mandatory = new UntypedFormControl(this.question.mandatory);
    this.default = new UntypedFormControl(this.question.default, {
      validators: Validators.compose([Validators.maxLength(255)])
    });
    this.min = new UntypedFormControl(this.question.min);
    this.max = new UntypedFormControl(this.question.max);
    this.maxLength = new UntypedFormControl(this.question.maxLength);
    this.hint = new UntypedFormControl(this.question.hint);
    this.valueFormat = new UntypedFormControl(this.question.valueFormat);
    this.allowDecimals = new UntypedFormControl();
    this.isTable = new UntypedFormControl(this.question.displayFieldType === "table");
    this.alwaysEditable = new UntypedFormControl(
      this.question.alwaysEditable || false
    );
    this.tag = new UntypedFormControl(this.question.tag);
    this.formGroup.addControl("questionType", this.questionType);
    this.formGroup.addControl("hidden", this.hidden);
    this.formGroup.addControl("complexityOverride", this.complexityOverride);
    this.formGroup.addControl("topLevel", this.topLevel);
    this.formGroup.addControl("questionName", this.questionName);
    this.formGroup.addControl("mandatory", this.mandatory);
    this.formGroup.addControl("default", this.default);
    this.formGroup.addControl("maxLength", this.maxLength);
    this.formGroup.addControl("hint", this.hint);
    this.formGroup.addControl("min", this.min);
    this.formGroup.addControl("max", this.max);
    this.formGroup.addControl("valueFormat", this.valueFormat);
    this.formGroup.addControl("allowDecimals", this.allowDecimals);
    this.formGroup.addControl("isTable", this.isTable);
    this.formGroup.addControl("displayFieldType", new UntypedFormControl());
    this.formGroup.addControl("tag", this.tag);
    this.formGroup.addControl("alwaysEditable", this.alwaysEditable);

    if (this.question.dataType === "Float") this.allowDecimals.setValue(true);
    else if (this.question.dataType === "integer")
      this.allowDecimals.setValue(false);

    this.formGroup.valueChanges.subscribe(formGroup => {
      Object.keys(formGroup).forEach(
        key => (this.question[key] = formGroup[key])
      );

      this.question.question = formGroup.questionName;
      this.question.mandatory = Boolean(formGroup.mandatory);

      if (formGroup.questionType === "number") {
        this.question.displayFieldType = "text";
        if (this.allowDecimals.value) {
          this.question.dataType = "Float";
        } else {
          this.question.dataType = "integer";
        }
      } else {
        this.question.displayFieldType = formGroup.questionType;
        this.question.dataType = "string";
        this.min.setValue(null, {
          emitEvent: false
        });
        this.max.setValue(null, {
          emitEvent: false
        });
        this.question.min = null;
        this.question.max = null;
      }

      if (formGroup.questionType === "checkbox" && formGroup.mandatory) {
        this.question.mandatory = false;
        this.mandatory.setValue("false", {
          emitEvent: false
        });
      }

      // table q cannot not also be a tab q
      if (formGroup.questionType === "table") {
        this.mandatory.setValue(false, {
          emitEvent: false
        });
        this.isTable.setValue(true, {
          emitEvent: false
        });
      } else {
        this.isTable.setValue(false, {
          emitEvent: false
        });
      }

      // update preview
      this.updatePreview();

      // need to re-init ui controls on values and table tab on question type change
      $(".ui.checkbox").checkbox();
    });

    // init all ui controls
    setTimeout(() => {
      this.initQuestionTypeDropdown();
      this.initDefaultValueDropdown();

      const values = [
        {
          name: "Select...",
          value: null,
          selected: !this.question.isCurrency && !this.question.isPercentage
        },
        {
          name: "Currency",
          value: "currency",
          selected: this.question.isCurrency
        },
        {
          name: "Percentage",
          value: "percentage",
          selected: this.question.isPercentage
        }
      ];

      $(this.numberOptionsDropDown.nativeElement).dropdown({
        placeholder: "Select...",
        fullTextSearch: "exact",
        preserveHTML: false,
        values: values,
        onChange: (value: string) => {
          this.question.isCurrency = value === "currency";
          this.question.isPercentage = value === "percentage";
        }
      });

      $("#page").click(() =>
        !this.clickedRow ? this.unfocusValueRows() : undefined
      );

      setTimeout(() => {
        $(".ui.checkbox").checkbox();
        $("#table.tab .new-value-btn").popup({
          on: "click",
          position: "right center"
        });
      });
    });
  }

  // re-init ui controls for tabs is required when question type changes
  goToTab(tabNum: number) {
    this.tab = tabNum;
  }

  initQuestionTypeDropdown() {
    const valuesOptions: {}[] = [
      {
        value: "text",
        name: "Text",
        selected: this.questionType.value === "text"
      },
      {
        value: "number",
        name: "Number",
        selected: this.questionType.value === "number"
      },
      {
        value: "textarea",
        name: "Textarea",
        selected: this.questionType.value === "textarea"
      },
      {
        value: "drop down",
        name: "Drop Down",
        selected: this.questionType.value === "drop down"
      },
      {
        value: "people",
        name: "People Picker",
        selected: this.questionType.value === "people"
      },
      {
        value: "radio",
        name: "Radio",
        selected: this.questionType.value === "radio"
      },
      {
        value: "date picker",
        name: "Date Picker",
        selected: this.questionType.value === "date picker"
      },
      {
        value: "checkbox",
        name: "Checkbox",
        selected: this.questionType.value === "checkbox"
      }
    ];

    // can only set unsaved questions to table
    if (
      !this.question.id ||
      (this.question.id && this.question.displayFieldType === "table")
    ) {
      valuesOptions.push({
        value: "table",
        name: "Table",
        selected: this.questionType.value === "table"
      });
    }

    $(this.questionTypeDropDown.nativeElement).dropdown({
      values: valuesOptions,
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        if (value) this.questionType.setValue(value);
        if (value === "table") {
          this.alwaysEditable.setValue(false);
        }
      }
    });
  }

  initDefaultValueDropdown() {
    if (
      !this.question.questionDropdowns ||
      (this.getQuestionType(this.question) !== "drop down" &&
        this.getQuestionType(this.question) !== "radio")
    )
      return;

    const defaultValueDropdownOptions = this.question.questionDropdowns
      .filter(v => !v.disabled)
      .sort((a, b) => a.order - b.order)
      .map(v => {
        return {
          value: v.value,
          name: v.display,
          selected: v.value === this.question.default
        };
      });

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        value: null,
        order: 0
      }
    ];

    $(this.defaultValueDropdown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(defaultValueDropdownOptions),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        this.default.setValue(value);
        // update values list to toggle default selected checkbox
        this.question.questionDropdowns.forEach(
          (v: ListOption) => (v.selected = v.value === value)
        );
      }
    });
  }

  // checks whether question's list options already contains a default selection
  questionHasDefault() {
    return this.question.questionDropdowns.some(d => d.selected);
  }

  deleteValue(index: number) {
    this.question.questionDropdowns[index].status = 99;
    this.initDefaultValueDropdown();
    this.updatePreview();
  }

  addValue() {
    this.question.questionDropdowns.unshift({
      id: null,
      value: null,
      display: null,
      order: null,
      selected: null,
      disabled: null,
      status: 0
    });

    // focus first input
    setTimeout(() => {
      this.question.questionDropdowns[0]["edit"] = true;
      $("#tbl-container")
        .find("#tbl-container tbody tr:first-of-type td:first-of-type input")
        .focus();
      $(".ui.checkbox").checkbox();
      this.updatePreview();
    });
  }

  editValue(valueOption: ListOption, event) {
    this.unfocusValueRows();
    valueOption.edit = true;
    $(event.target)
      .find("input")
      .focus();
    this.clickedRow = true; // prevent unfocusValueRows() causing row to de-activate
    setTimeout(() => (this.clickedRow = false));
  }

  // only 1 option cab be defaut selected. toggle all other options
  toggleDefaultValue(valueOption: ListOption) {
    const value = cloneDeep(valueOption);
    this.question.questionDropdowns.forEach((v: ListOption) => {
      v.selected = false;
      if (v.id === value.id) v.selected = !value.selected;
      if (v.selected) this.question.default = value.value;
    });
    if (!this.question.questionDropdowns.some(v => v.selected))
      this.question.default = null;
    this.initDefaultValueDropdown();
  }

  // update question preview
  updatePreview() {
    if (!this.question) return;
    if (this.questionType.value !== "table") {
      this.prevFormGroup.removeControl(String(this.question.id));
      setTimeout(() => {
        this.prevFormGroup.addControl(
          String(this.question.id),
          makeControlForQuestion(this.question)
        );
      });
    }
  }

  addExistingQuestion() {
    $(this.addQuestionModal.nativeElement).modal("show");
    $("#table.tab .new-value-btn").popup("hide");
  }

  // question can only be added to table if it is not already used, is not a table itself, is not the same question id, and is not tab question
  canAddTableQuestion(question: IFormField) {
    return (
      !this.columnQuestions.find(q => q.id === question.id) &&
      question.displayFieldType !== "table" &&
      !question.parentId
    );
  }

  // sets the table question as the selected question's parentId
  useQuestion(question) {
    this.columnQuestions.push({
      ...question,
      updateParentId: true
    });
    $(this.addQuestionModal.nativeElement).modal("hide");
  }

  // remove question from table
  removeQuestion(question) {
    this.columnQuestions = this.columnQuestions.filter(
      q => q.id !== question.id
    );
  }

  save() {
    this.isSaving = true;

    const reqBody = {
      id: this.question.id || null,
      displayFieldType: this.question.displayFieldType,
      isTab: this.question.isTab,
      hidden: this.question.hidden,
      complexityOverride: this.question.complexityOverride,
      topLevel: this.question.topLevel,
      question: this.question.question,
      dataType: this.question.dataType,
      mandatory: this.question.mandatory,
      default: this.question.default,
      min: this.question.min,
      max: this.question.max,
      maxLength: this.question.maxLength,
      valueFormat: this.question.valueFormat,
      hint: this.question.hint,
      APSRef: this.question.APSRef,
      APSSection: this.question.APSSection,
      APSSubsection: this.question.APSSubsection,
      APSQuestionGrouping: this.question.APSQuestionGrouping,
      APSDimension: this.question.APSDimension,
      APSOrder: this.question.APSOrder,
      APSCalcField: this.question.APSCalcField,
      tacRef: this.question.tacRef,
      isCurrency: this.question.isCurrency,
      isPercentage: this.question.isPercentage,
      dependantMandatoryParentId: this.question.dependantMandatoryParentId,
      dependantMandatoryParentValue: this.question
        .dependantMandatoryParentValue,
      questionDropdowns: this.question.questionDropdowns,
      year: this.question.year,
      medicareCoordinates: this.question.medicareCoordinates,
      transmittalLetterRef: this.question.transmittalLetterRef,
      tag: this.question.tag,
      taxServiceIdentifier: this.question.taxServiceIdentifier,
      isManualAPSEntry: this.question.isManualAPSEntry,
      lodgementBotField: this.question.lodgementBotField,
      alwaysEditable: this.question.alwaysEditable
    };

    if (this.question.dependantMandatoryParentValue != null)
      reqBody.dependantMandatoryParentValue = String(
        this.question.dependantMandatoryParentValue
      );

    // convert to required data types
    if (this.getQuestionType(this.question) === "number") {
      const parseNumber = x => {
        if (x == null) {
          return null;
        }
        const n = Number(x);
        return Number.isFinite(n) ? n : null;
      };

      reqBody.default =
        reqBody.default != null ? String(reqBody.default) : null;
      reqBody.min = parseNumber(reqBody.min);
      reqBody.max = parseNumber(reqBody.max);
      reqBody.maxLength = parseNumber(reqBody.maxLength);
    } else if (this.getQuestionType(this.question) === "date picker") {
      reqBody.dataType = "date";
    } else if (this.getQuestionType(this.question) === "table") {
      reqBody.dataType = "table";
    }
    // tables require checkboxes to be boolean
    else if (this.getQuestionType(this.question) === "checkbox") {
      reqBody.dataType = "boolean";
    } else {
      reqBody.dataType = "string";
    }

    // question dropdowns can cause BE error if too large (dorpdowns are saved separately)
    reqBody.questionDropdowns = [];

    let getTaxReturnStructureObs;
    let qUpdateObs = [];
    const saveColumnQuestions = questionId => {
      const handleColumnQuestions = () => {
        this.columnQuestions.forEach((q: any) => {
          if (q.updateParentId) {
            qUpdateObs.push(
              this.questionService.updateGlobalQuestion({
                id: q.id,
                parentId: questionId
              })
            );

            // for existing table q, the column q also needs to be associated to sc and sg of which the tbl question belongs
            if (this.question.id) {
              if (
                subCategory &&
                !subCategory.questions.find(_q => _q.id === q.id)
              ) {
                qUpdateObs.push(
                  this.subcategoryService.createSubcategoryQuestionAssociation(
                    subCategory.id,
                    {
                      questionId: q.id
                    }
                  )
                );
              }

              if (subGroup && !subGroup.questions.find(_q => _q.id === q.id)) {
                qUpdateObs.push(
                  this.subgroupService.addSubGroupQuestionAssociation(
                    subGroup.id,
                    {
                      questionId: q.id
                    }
                  )
                );
              }
            }
          }
        });
      };

      // for existing table q, the column q also needs to be associated to sc and sg of which the tbl question belongs
      let subCategory;
      let subGroup;
      if (this.question.id) {
        getTaxReturnStructureObs = this.taxReturnService
          // we may need to get all of the structures as the taxReturnTypeId may not have been set yet to the table question
          .getGlobalStructure().pipe(
          map((taxStructure: any) => {
            taxStructure.forEach(c => {
              c.subcategories.forEach(sc => {
                if (sc.questions.find(_q => _q.id === this.question.id)) {
                  subCategory = sc;
                  sc.subgroups.forEach(sg => {
                    if (sg.questions.find(_q => _q.id === this.question.id))
                      subGroup = sg;
                  });
                }
              });
            });
            handleColumnQuestions();
          }));
      } else {
        handleColumnQuestions();
      }
    };

    const updateDropdown = questionId => {
      if (this.question.questionDropdowns) {
        this.question.questionDropdowns.forEach(qD => {
          if (qD.id) {
            const originalDp = this.originalQuestion.questionDropdowns.find(
              _qD => _qD.id === qD.id
            );
            const hasChanged =
              qD.value !== originalDp.value ||
              qD.display !== originalDp.display ||
              qD.order !== originalDp.order ||
              qD.selected !== originalDp.selected ||
              qD.disabled !== originalDp.disabled ||
              qD.status !== originalDp.status;

            if (hasChanged)
              qUpdateObs.push(
                this.dropdownService.updateDropdown({
                  id: qD.id,
                  value: qD.value || qD.display || "",
                  display: qD.display,
                  order: qD.order,
                  selected: qD.selected,
                  disabled: qD.disabled,
                  questionId: questionId,
                  status: qD.status
                })
              );
          } else if (qD.value || qD.display) {
            qUpdateObs.push(
              this.dropdownService.addDropdown({
                value: qD.value || qD.display,
                display: qD.display,
                order: qD.order,
                selected: qD.selected,
                disabled: qD.disabled,
                questionId: questionId,
                status: qD.status
              })
            );
          }
        });
      }
    };

    // for new question, need to get q id before saving table questions
    if (!this.question.id) {
      this.questionService.addGlobalQuestion(reqBody).subscribe(
        (q: IFormField) => {
          if (q.displayFieldType === "table") saveColumnQuestions(q.id);
          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          )
            updateDropdown(q.id);

          if (qUpdateObs.length > 0) {
            forkJoin(qUpdateObs).subscribe(
              () => {
                // refresh route to allow new question id to appear in url
                this.router.navigate(["settings", "global", "questions", q.id]);
                this.isSaving = false;
              },
              () => (this.isSaving = false)
            );
          } else {
            this.router.navigate(["settings", "global", "questions", q.id]);
            this.isSaving = false;
          }
        },
        () => (this.isSaving = false)
      );
    }

    // existing question
    else {
      if (this.question.displayFieldType === "table")
        saveColumnQuestions(this.question.id);
      if (
        this.question.displayFieldType === "drop down" ||
        this.question.displayFieldType === "radio"
      ) {
        updateDropdown(this.question.id);
      }

      let savedQ;
      qUpdateObs.push(
        this.questionService
          .updateGlobalQuestion(reqBody).pipe(
            map((q: Question) => (savedQ = q))
          )
      );

      const handleUpdateObs = () => {
        forkJoin(qUpdateObs).subscribe(
          () => {
            this.questionService
              .getglobalQuestionById(this.question.id)
              .subscribe((data: IFormField) => {
                this.initQuestion(data);
                this.isSaving = false;
              });
          },
          () => (this.isSaving = false)
        );
      };

      if (getTaxReturnStructureObs) {
        forkJoin(getTaxReturnStructureObs).subscribe(() => {
          handleUpdateObs();
        });
      } else {
        handleUpdateObs();
      }
    }
  }

  syncCache() {
    this.isSyncingCache = true;
    this.taxReturnService.syncGlobalStructure().subscribe(
      () => {
        this.isSyncingCache = false;
      },
      () => {
        this.isSyncingCache = false;
      }
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
